import { useEffect, useState } from "react";
import { Trash2, RotateCcw, AlertTriangle, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/utils/formatINR";

export default function Trash() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [projectRows, setProjectRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [npds, prjs] = await Promise.all([
        api.get("/npd/trash/list"),
        api.get("/projects/trash/list"),
      ]);
      setRows(npds.data.npds || []);
      setProjectRows(prjs.data.projects || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Trash");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (user?.role !== "admin") {
    return <div className="p-6 text-slate-500">Trash is admin-only.</div>;
  }

  const restore = async (npd) => {
    if (!window.confirm(`Restore ${npd.npd_number} (${npd.part_name})?`)) return;
    try {
      await api.post(`/npd/${npd.npd_id}/restore`);
      toast.success(`Restored ${npd.npd_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const restoreProject = async (prj) => {
    const childMsg = prj.child_count > 0
      ? `\n\nThis will also restore ${prj.child_count} child part(s) that were deleted together.`
      : "";
    if (!window.confirm(`Restore project ${prj.project_number} (${prj.project_name})?${childMsg}`)) return;
    try {
      await api.post(`/projects/${prj.project_id}/restore`);
      toast.success(`Restored ${prj.project_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="trash-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Trash2 size={22} className="text-slate-500" /> Trash
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Soft-deleted NPDs and projects. Records remain in the database and can be restored. Admin only.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 mb-4 flex items-start gap-2 text-sm text-amber-800">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
        <span>Drawing files in Object Storage are not removed on soft-delete. Only the database record is marked. Restoring a project also restores all child parts that were deleted together.</span>
      </div>

      {/* Deleted Projects */}
      <div className="bg-white border border-slate-200 rounded-lg mb-6">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Briefcase size={16} className="text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-700">Deleted Projects ({projectRows.length})</h2>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : projectRows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">No deleted projects.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trash-projects-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-3 font-semibold">PRJ #</th>
                  <th className="text-left p-3 font-semibold">Project</th>
                  <th className="text-center p-3 font-semibold">Children</th>
                  <th className="text-left p-3 font-semibold">Deleted At</th>
                  <th className="text-left p-3 font-semibold">Deleted By</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {projectRows.map((p) => (
                  <tr key={p.project_id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs text-slate-600">{p.project_number}</td>
                    <td className="p-3">
                      <div className="font-medium text-slate-900">{p.project_name}</div>
                      <div className="text-xs text-slate-500">{p.end_customer ? `End: ${p.end_customer}` : ""}</div>
                    </td>
                    <td className="p-3 text-center font-mono">{p.child_count ?? 0}</td>
                    <td className="p-3 text-slate-700 text-xs">
                      {p.deleted_at ? new Date(p.deleted_at).toLocaleString("en-IN") : "—"}
                    </td>
                    <td className="p-3 text-slate-700 text-xs">{p.deleted_by_name || "—"}</td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={() => restoreProject(p)}
                        className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 text-sm font-medium"
                        data-testid={`trash-project-restore-${p.project_number}`}
                      >
                        <RotateCcw size={14} /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deleted NPDs */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-5 py-3 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-700">Deleted NPDs ({rows.length})</h2>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">No deleted NPDs.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trash-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-3 font-semibold">NPD #</th>
                  <th className="text-left p-3 font-semibold">Part</th>
                  <th className="text-left p-3 font-semibold">Deleted At</th>
                  <th className="text-left p-3 font-semibold">Deleted By</th>
                  <th className="text-right p-3 font-semibold">Grand Total</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((n) => (
                  <tr key={n.npd_id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs text-slate-600">{n.npd_number}</td>
                    <td className="p-3">
                      <div className="font-medium text-slate-900 truncate max-w-xs">{n.part_name || "—"}</div>
                      <div className="text-xs text-slate-500">{n.part_number || ""}</div>
                    </td>
                    <td className="p-3 text-slate-700 text-xs">
                      {n.deleted_at ? new Date(n.deleted_at).toLocaleString("en-IN") : "—"}
                    </td>
                    <td className="p-3 text-slate-700 text-xs">{n.deleted_by_name || "—"}</td>
                    <td className="p-3 text-right font-mono">{formatINR(n.grand_total)}</td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={() => restore(n)}
                        className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 text-sm font-medium"
                        data-testid={`trash-restore-${n.npd_number}`}
                      >
                        <RotateCcw size={14} /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
